/**
 * @file
 * @copyright  2017 Subscribe-HR
 */

import ConditionList from './ConditionList';

/**
 * Subscribe-HR entity settings
 */
export default class EntitySettings {

  /**
   * Create entity settings.
   * @param {array} listAllFields
   * @param {ConditionList} conditionList
   * @param {int} currentPage
   */
  constructor(listAllFields = [], conditionList = null, currentPage = 1, itemsPerPage = 10) {
    this.setListAllFields(listAllFields);
    this.conditionList = conditionList;
    this.setCurrentPage(currentPage);
    this.setItemsPerPage(itemsPerPage);
    this.listAllFieldsByNameLookup = null;
  }

  /**
   * Returns list all fields.
   * @return {array}
   */
  getListAllFields() {
    return this.listAllFields;
  }

  /**
   * Returns list all field names.
   * @return {array}
   */
  getListAllFieldNames() {
    return this.getListAllFields().map((field) => {
      return field.Name;
    });
  }

  /**
   * Sets list all fields.
   * @param {array} listAllFields - list all fields
   */
  setListAllFields(listAllFields) {
    this.listAllFields = listAllFields;
  }

  /**
   * Check if list all field exists.
   * @param {string} name - field name
   * @return {bool}
   */
  hasListAllField(name) {
    this.createListAllFieldsByNameLookup();
    if (!Object.prototype.hasOwnProperty.call(this.listAllFieldsByNameLookup, name)) {
      return false;
    }
    return true;
  }

  /**
   * Check if list all field exists.
   * @param {string} name - field name
   */
  removeListAllField(name) {
    if (this.hasListAllField(name)) {
      this.listAllFieldsByNameLookup = null;
      this.listAllFields = this.getListAllFields().reduce((result, field) => {
        if (field.Name !== name) {
          return [...result, field];
        }
        return result;
      }, []);
    }
  }

  /**
   * Check if list all field exists.
   * @param {object} field - field to add
   */
  addListAllField(field) {
    this.listAllFieldsByNameLookup = null;
    this.listAllFields.push(field);
  }

  /**
   * Returns list all field.
   * @param {string} name - field name
   * @return {object}
   */
  getListAllField(name) {
    if (this.hasListAllField(name)) {
      return this.listAllFieldsByNameLookup[name];
    }
    return null;
  }

  /**
   * @private
   * Creates lookup to find fields by labels
   */
  createListAllFieldsByNameLookup = () => {
    if (this.listAllFieldsByNameLookup === null) {
      this.listAllFieldsByNameLookup = this.getListAllFields().reduce((result, item) => {
        return {...result, [item.Name]: item};
      }, {});
    }
  };

  /**
   * Returns list all fields.
   * @return {ConditionList}
   */
  getConditionList() {
    return this.conditionList;
  }

  /**
   * Set condition list.
   * @param {ConditionList} conditionList
   */
  setConditionList(conditionList) {
    this.conditionList = conditionList;
  }

  /**
   * Set current page.
   * @param {int} currentPage
   */
  setCurrentPage(currentPage) {
    this.currentPage = currentPage;
  }

  /**
   * Get current page.
   * @return {int}
   */
  getCurrentPage() {
    return this.currentPage;
  }

  /**
   * Set items per page.
   * @param {int} itemsPerPage
   */
  setItemsPerPage(itemsPerPage) {
    this.itemsPerPage = itemsPerPage;
  }

  /**
   * Get items per page.
   * @return {int}
   */
  getItemsPerPage() {
    return this.itemsPerPage;
  }

  /**
   * Changes field sorting direction.
   * @param {string} name - field name
   * @param {string} sort - sorting direction
   */
  setListAllFieldSort(name, sort) {
    for (let i = 0; i < this.listAllFields.length; i += 1) {
      if (this.listAllFields[i].Name === name) {
        this.listAllFields[i].Sort = (sort === undefined ? null : sort);
      }
    }
  }

  /**
   * Returns true if one of the fields has sorting applied to it.
   * @return {bool}
   */
  hasFieldSorting() {
    for (let i = 0; i < this.listAllFields.length; i += 1) {
      if (this.listAllFields[i].Sort !== null) {
        return true;
      }
    }
    return false;
  }

  /**
   * Changes field sorting direction.
   * @param {string} name - field name
   * @param {string} sort - sorting direction
   */
  moveListAllField(name, position) {
    const result = [];
    for (let i = 0; i < this.listAllFields.length; i += 1) {
      if (i === position) {
        result.push(this.getListAllField(name));
      }
      if (this.listAllFields[i].Name !== name) {
        result.push(this.listAllFields[i]);
      }
    }
    this.setListAllFields(result);
  }

  /**
   * Returns list of sort fields.
   * @return {array}
   */
  getSortFields(fields = null) {
    let output = [];
    let sortFields = fields;
    if (sortFields === null) {
      sortFields = this.getListAllFields().reduce((result, item) => {
        if (item.Sort !== null) {
          return {...result, [item.Name]: item.Sort};
        }
        return result;
      }, {});
    }

    if (Object.keys(sortFields).length > 0) {
      output = this.getListAllFields().reduce((result, item) => {
        if (sortFields[item.Name] !== undefined) {
          return [...result, `${item.Name} ${sortFields[item.Name]}`];
        }
        return result;
      }, []);
    }
    return output;
  }

  /**
   * Load from metadata.
   * @param {object} metadata
   * @return {EntitySettings}
   */
  load(metadata) {
    this.listAllFields = metadata.ListAllFields;
    this.setCurrentPage(metadata.CurrentPage);
    this.setItemsPerPage(metadata.ItemsPerPage);
    if (metadata.ConditionList !== null) {
      this.conditionList = new ConditionList();
      this.conditionList.load(metadata.ConditionList);
    }
    return this;
  }

  /**
   * Get json definition.
   * @param {object} metadata
   * @return {EntitySettings}
   */
  getValue() {
    return {
      ListAllFields: this.listAllFields,
      ConditionList: this.conditionList !== null ? this.conditionList.getDefinition() : null,
      CurrentPage: this.getCurrentPage(),
      ItemsPerPage: this.getItemsPerPage()
    };
  }
}



// WEBPACK FOOTER //
// ./src/client/common/js-subscribe-sdk/lib/Entities/EntitySettings.js